import { useLocation } from "wouter";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Sparkles,
  Zap,
  Infinity,
  CreditCard,
  FolderKanban,
  ArrowRight,
  Check,
  Crown,
  X,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export type PlanLimitCode =
  | "PLAN_LIMIT_PROJECTS"
  | "PLAN_LIMIT_DAILY_RUNS"
  | "PLAN_LIMIT_CREDITS";

interface UpgradePromptProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  limitCode: PlanLimitCode;
  limit?: number;
  current?: number;
}

const LIMIT_CONFIG: Record<
  PlanLimitCode,
  {
    icon: typeof Sparkles;
    title: string;
    subtitle: (limit?: number) => string;
    accentColor: string;
    glowColor: string;
  }
> = {
  PLAN_LIMIT_PROJECTS: {
    icon: FolderKanban,
    title: "Project limit reached",
    subtitle: (limit) =>
      `You've hit the ${limit ?? 3}-project cap on the Free plan. Upgrade to keep building.`,
    accentColor: "text-amber-400",
    glowColor: "from-amber-500/20 via-orange-500/10",
  },
  PLAN_LIMIT_DAILY_RUNS: {
    icon: Zap,
    title: "Daily run limit reached",
    subtitle: (limit) =>
      `All ${limit ?? 10} daily agent runs are used up. Upgrade for unlimited runs.`,
    accentColor: "text-sky-400",
    glowColor: "from-sky-500/20 via-blue-500/10",
  },
  PLAN_LIMIT_CREDITS: {
    icon: CreditCard,
    title: "Credits depleted",
    subtitle: () =>
      `Your credit balance has run out. Upgrade to get monthly credits included.`,
    accentColor: "text-emerald-400",
    glowColor: "from-emerald-500/20 via-teal-500/10",
  },
};

const PRO_FEATURES = [
  { icon: Infinity, label: "Unlimited projects", free: "3 projects" },
  { icon: Zap, label: "Unlimited daily runs", free: "10 / day" },
  { icon: CreditCard, label: "$10 credits included", free: "$5 credits" },
  { icon: Crown, label: "Priority support", free: "Community" },
];

export function UpgradePrompt({
  open,
  onOpenChange,
  limitCode,
  limit,
  current,
}: UpgradePromptProps) {
  const [, setLocation] = useLocation();
  const config = LIMIT_CONFIG[limitCode];
  const LimitIcon = config.icon;

  const handleUpgrade = () => {
    onOpenChange(false);
    setLocation("/billing");
  };

  // Progress bar: how full the limit is
  const usagePercent =
    limit && current != null ? Math.min((current / limit) * 100, 100) : 100;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px] p-0 gap-0 overflow-hidden border-white/[0.06] bg-[hsl(240,6%,10%)] shadow-2xl [&>button]:hidden" aria-describedby={undefined}>
        <DialogTitle className="sr-only">Upgrade your plan</DialogTitle>
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            >
              {/* Ambient glow */}
              <div
                className={`absolute inset-0 bg-gradient-to-br ${config.glowColor} to-transparent opacity-60 pointer-events-none`}
              />

              {/* Close button */}
              <button
                onClick={() => onOpenChange(false)}
                className="absolute right-3 top-3 z-10 rounded-full p-1.5 text-white/30 hover:text-white/60 hover:bg-white/5 transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>

              {/* Header */}
              <div className="relative px-6 pt-7 pb-5">
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08, duration: 0.35 }}
                  className="flex items-start gap-4"
                >
                  {/* Icon badge */}
                  <div className="relative shrink-0">
                    <div
                      className={`w-11 h-11 rounded-xl bg-gradient-to-br ${config.glowColor} to-white/[0.03] border border-white/[0.08] flex items-center justify-center`}
                    >
                      <LimitIcon className={`w-5 h-5 ${config.accentColor}`} />
                    </div>
                    {/* Pulse dot */}
                    <span className="absolute -top-0.5 -right-0.5 flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-60" />
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-400" />
                    </span>
                  </div>

                  <div className="min-w-0 space-y-1.5">
                    <h2 className="text-[15px] font-semibold text-white tracking-tight leading-tight">
                      {config.title}
                    </h2>
                    <p className="text-[13px] text-white/50 leading-relaxed">
                      {config.subtitle(limit)}
                    </p>
                  </div>
                </motion.div>

                {/* Usage bar */}
                {limit != null && (
                  <motion.div
                    initial={{ opacity: 0, scaleX: 0.3 }}
                    animate={{ opacity: 1, scaleX: 1 }}
                    transition={{ delay: 0.2, duration: 0.5, ease: "easeOut" }}
                    className="mt-4 origin-left"
                  >
                    <div className="flex items-center justify-between text-[11px] text-white/35 mb-1.5 tabular-nums">
                      <span>Usage</span>
                      <span>
                        {current ?? limit} / {limit}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${usagePercent}%` }}
                        transition={{
                          delay: 0.35,
                          duration: 0.7,
                          ease: [0.16, 1, 0.3, 1],
                        }}
                        className="h-full rounded-full bg-gradient-to-r from-amber-500 via-orange-500 to-red-500"
                      />
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Divider */}
              <div className="h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />

              {/* Feature comparison */}
              <div className="px-6 py-5">
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15, duration: 0.35 }}
                >
                  {/* Column headers */}
                  <div className="grid grid-cols-[1fr,80px,80px] gap-2 mb-2">
                    <div />
                    <span className="text-[10px] uppercase tracking-widest text-white/25 text-center">
                      Free
                    </span>
                    <span className="text-[10px] uppercase tracking-widest text-amber-400/70 text-center font-medium">
                      Pro
                    </span>
                  </div>

                  {/* Feature rows */}
                  <div className="space-y-0">
                    {PRO_FEATURES.map(
                      ({ icon: Icon, label, free }, i) => (
                        <motion.div
                          key={label}
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{
                            delay: 0.22 + i * 0.06,
                            duration: 0.3,
                          }}
                          className="grid grid-cols-[1fr,80px,80px] gap-2 items-center py-2 border-b border-white/[0.03] last:border-0"
                        >
                          <div className="flex items-center gap-2.5">
                            <Icon className="w-3.5 h-3.5 text-white/30 shrink-0" />
                            <span className="text-[13px] text-white/70">
                              {label}
                            </span>
                          </div>
                          <span className="text-[12px] text-white/30 text-center">
                            {free}
                          </span>
                          <div className="flex justify-center">
                            <div className="w-5 h-5 rounded-full bg-amber-400/10 flex items-center justify-center">
                              <Check className="w-3 h-3 text-amber-400" />
                            </div>
                          </div>
                        </motion.div>
                      ),
                    )}
                  </div>
                </motion.div>
              </div>

              {/* Divider */}
              <div className="h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />

              {/* CTA section */}
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.35 }}
                className="px-6 pt-5 pb-6 space-y-3"
              >
                {/* Price anchoring */}
                <div className="flex items-baseline justify-center gap-1.5 mb-1">
                  <span className="text-2xl font-bold text-white tracking-tight">
                    $19
                  </span>
                  <span className="text-[13px] text-white/35">/month</span>
                </div>

                <Button
                  onClick={handleUpgrade}
                  className="w-full h-11 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black font-semibold text-[14px] rounded-lg shadow-lg shadow-amber-500/20 transition-all duration-200 hover:shadow-amber-500/30 hover:scale-[1.01] active:scale-[0.99] border-0"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Upgrade to Pro
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>

                <button
                  onClick={() => onOpenChange(false)}
                  className="w-full text-center text-[13px] text-white/30 hover:text-white/50 transition-colors py-1.5 cursor-pointer"
                >
                  Maybe later
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
